import { useState, useCallback, useEffect } from 'react';
import VideoCatalogue from '../services/VideoCatalogue';

// 🔧 LAZY IMPORT: Import http only when needed to avoid circular dependencies
const getHttpClient = async () => {
    const { default: http } = await import('../apis/http');
    return http;
};

/**
 * Custom hook for video data management
 * Provides clean, consistent video data across all components
 */
const useVideos = (type = 'list', filters = {}, autoFetch = false) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const fetchData = useCallback(async () => {
        // 🔧 SECURITY: Always require userId for authenticated API calls
        if (!filters.userId) {
            console.log('⏳ useVideos: Waiting for valid userId...', { userId: filters.userId, type });
            return;
        }
        
        // 🔧 VALIDATION: Ensure required parameters are present
        if (type === 'detail' && !filters.videoId) {
            console.log('⚠️ useVideos: Video detail requires videoId', { videoId: filters.videoId });
            return;
        }
        
        setLoading(true);
        setError(null);
        
        try {
            // 🔧 LAZY LOAD: Get http client only when needed
            const http = await getHttpClient();
            VideoCatalogue.setHttpClient(http);
            
            let response;
            
            switch (type) {
                case 'category':
                    response = await VideoCatalogue.getVideosByCategory(filters);
                    break;
                case 'detail':
                    console.log('🎬 useVideos: Fetching video detail for videoId:', filters.videoId, 'userId:', filters.userId || 'none');
                    response = await VideoCatalogue.getVideoDetail(filters.videoId, filters.userId);
                    console.log('🎬 useVideos: Video detail response:', response?.isSuccess ? 'SUCCESS' : 'FAILED', response?.data?.title || 'no title');
                    break;
                case 'related':
                    response = await VideoCatalogue.getRelatedVideos(filters);
                    break;
                case 'list':
                default:
                    response = await VideoCatalogue.getVideosList(filters);
                    break;
            }
            
            if (response?.isSuccess) {
                console.log('✅ useVideos: Data fetched successfully for type:', type);
                if (type === 'detail') {
                    console.log('🎬 useVideos: Video detail data:', response.data);
                }
                setData(response.data);
            } else {
                console.error('❌ useVideos: Failed to fetch data for type:', type, 'Error:', response?.message);
                setError(response?.message || 'Failed to fetch videos');
            }
        } catch (err) {
            console.error('useVideos error:', err);
            setError(err?.message || 'An error occurred while fetching videos');
        } finally {
            setLoading(false);
        }
    }, [type, filters.userId, filters.videoId, filters.categoryId, filters.pageSize, filters.pageNumber, filters.searchName, JSON.stringify(filters.keywordIds)]);
    
    // Auto-fetch when filters change if autoFetch is enabled
    useEffect(() => {
        if (autoFetch) {
            fetchData();
        }
    }, [autoFetch, fetchData]);
    
    // 🔧 SECURITY FIX: Auto-retry when userId becomes available
    useEffect(() => {
        if (filters.userId && !data && !loading && !error) {
            console.log('🔄 useVideos: userId now available, auto-retrying fetch...', { userId: filters.userId, type });
            fetchData();
        }
    }, [filters.userId, data, loading, error, fetchData]);
    
    return {
        data,
        loading,
        error,
        fetchData,
        refetch: fetchData
    };
};

export default useVideos;
